/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useState } from "react";
import useWinRate from "./useWinRate";   // you’ll create this
import InputControls from "./InputControls";
import ResultsDisplay from "./ResultsDisplay";
import Presets from "./Presets";
import ScenarioManager from "./ScenarioManager";
import { useProfitProfiles } from "@/hooks/useProfitProfiles";
import * as Charts from "./charts";

export default function WinRateCalculator() {
  const [inputs, setInputs] = useState({
    growthGoal: 10,
    riskPerTrade: 0.5,
    numTrades: 30,
    profile: "Intraday",
    splits: {
      w1: 40, w2: 30, w3: 30,
      R1: 1,  R2: 2,  R3: 3,
      q1: 100, q2: 0,  q3: 0,
    },
  });

  const { A, pMin, contributions } = useWinRate(inputs);
  const { profiles, create } = useProfitProfiles();

  const handlePresetSelect = (selectedProfile) => {
    const newSplits = {
      w1: 0, R1: 0, q1: 0,
      w2: 0, R2: 0, q2: 0,
      w3: 0, R3: 0, q3: 0,
    };

    // Profile's partial_targets are: { r_multiple, size_pct }. No fill_expectancy_percent (q values).
    if (selectedProfile && selectedProfile.partial_targets) {
      selectedProfile.partial_targets.forEach((target, index) => {
        if (index === 0) {
          newSplits.w1 = target.size_pct || 0; // Use size_pct for weight
          newSplits.R1 = target.r_multiple || 0;
          // q1 will remain 0 as it's not in preset data
        } else if (index === 1) {
          newSplits.w2 = target.size_pct || 0; // Use size_pct for weight
          newSplits.R2 = target.r_multiple || 0;
          // q2 will remain 0
        } else if (index === 2) {
          newSplits.w3 = target.size_pct || 0; // Use size_pct for weight
          newSplits.R3 = target.r_multiple || 0;
          // q3 will remain 0
        }
      });
    }

    setInputs(prevInputs => ({
      ...prevInputs, // Keeps existing growthGoal, riskPerTrade, numTrades
      profile: selectedProfile.name || prevInputs.profile, // Update profile name
      splits: newSplits,
    }));
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div>
        <InputControls inputs={inputs} onChange={setInputs} />
        <Presets profiles={profiles} onSelect={handlePresetSelect} />
        <ScenarioManager inputs={inputs} onLoad={setInputs} />
      </div>
      <div>
        <ResultsDisplay pMin={pMin} contributions={contributions} averageRR={A} />
        <Charts.LineChart inputs={inputs} pMin={pMin} />
        {/* add Heatmap & BarChart as needed */}
      </div>
    </div>
  );
}
